import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import classNames from "classnames";

import Title from "./Title";

import { Get } from "../../shared/services/Fetch";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../shared/context/AuthContext";
import { getFormattedDate } from "../../shared/utils/DateUtil";
import { getFormattedCurrency } from "../../shared/utils/Global";

import { WorkflowLog } from "../WorkflowTimeline/interface";
import { DeviationProps, ProcessType, LoanDeviation } from "./interface";
import { LoanApplicationService } from "../../../unified-dashboard/src/services/LoanApplication";

type PricingParams = {
  previousInterestRate?: number | string;
  previousProcessingFee?: number | string;
  proposedInterestRate?: number | string;
  proposedProcessingFee?: number | string;
};

type WorkflowHistoryItem = {
  action?: string;
  actionBy?: string;
  createdAt?: string;
  remarks?: string;
  metadataJson?: {
    remarks?: string;
    task?: {
      metadata?: {
        offerNegotiationParams?: PricingParams;
      };
    };
  };
};

type TimelineRow = {
  actionBy: string;
  status: string;
  createdAt: string;
  comments: string;
  interestRate: ProcessType;
  processingFee: ProcessType;
};

const TEXT_COLOR = {
  cancelled: "text-[#B42318]",
  rejected: "text-[#B42318]",
  accepted: "text-[#05603A]",
  approved: "text-[#05603A]",
  processing: "text-[#3290FE]"
} as const;

function mapWorkflowHistoryToTimeline(
  history: WorkflowHistoryItem[] = []
): TimelineRow[] {
  return history.map((item) => {
    const task = item?.metadataJson?.task ?? {};
    const metadata = task?.metadata ?? {};
    const pricing: any =
      (metadata as any)?.offerNegotiationParams ?? (metadata as any);

    return {
      actionBy: item?.actionBy ?? "",
      status: item?.action ?? "",
      createdAt: item?.createdAt ?? "",
      comments: item?.metadataJson?.remarks ?? item?.remarks ?? "",
      interestRate: {
        previous: String(pricing?.previousInterestRate ?? ""),
        proposed: String(pricing?.proposedInterestRate ?? ""),
        deviation:
          Number(pricing?.previousInterestRate) !==
          Number(pricing?.proposedInterestRate)
      },
      processingFee: {
        previous: String(pricing?.previousProcessingFee ?? ""),
        proposed: String(pricing?.proposedProcessingFee ?? ""),
        deviation:
          Number(pricing?.previousProcessingFee) !==
          Number(pricing?.proposedProcessingFee)
      }
    };
  });
}

function transformTimelineResponse(wfTimeline: WorkflowLog[]): TimelineRow[] {
  return wfTimeline
    .filter((wf) => !wf.action.startsWith("Email"))
    .map((wf) => wf.additionalParams?.params as LoanDeviation);
}

export default function Timeline(props: DeviationProps) {
  const [timelineInfo, setTimelineInfo] = useState<TimelineRow[]>([]);

  const { loanID } = useParams();

  const { fetchToken } = GetAuthContext();
  const token = fetchToken();

  const getWorkflowTimeline = async () => {
    const headers = new Headers();
    headers.append("page-header", props.pageName!);

    const response: any = await Get(
      `/masterDashboard/getWorkflowTimeline?requestID=${props?.timelineInfo?.[0]?.requestID}`,
      token,
      headers
    );

    if (!response.status) {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
      return;
    }

    setTimelineInfo(
      transformTimelineResponse(response.data?.steps as WorkflowLog[])
    );
  };

  const getDeviationHistory = useCallback(async () => {
    try {
      const payload = {
        graphQLAction: "GetWorkflowHistory",
        dashboardType: "masterDashboard",
        workflowInstanceID: props?.loanDeviationInfo?.workflowInstanceId ?? "",
        sourceEntityID: props?.sourceEntityID ?? "",
        loanApplicationID: loanID as string
      };

      const response: any = await LoanApplicationService.fetchGraphqlData({
        token,
        pageName: props.pageName as string,
        payload
      });

      if (response?.status) {
        const history = response?.data?.workflowInstanceHistory ?? [];
        setTimelineInfo(mapWorkflowHistoryToTimeline(history));
        notify({
          title: "Success",
          type: "success",
          text: "Deviation history fetched successfully"
        });
      } else {
        notify({
          title: "Error",
          type: "error",
          text: response?.error || "Failed to fetch deviation history"
        });
        return;
      }
    } catch (err: any) {
      console.error("error", err);
      notify({
        title: "Error",
        type: "error",
        text: err?.message || "Unexpected error while fetching timeline"
      });
    }
  }, [
    token,
    props.pageName,
    props?.loanDeviationInfo?.workflowInstanceId,
    props?.sourceEntityID,
    loanID
  ]);

  useEffect(() => {
    if (props?.isABFLBL) getWorkflowTimeline();

    if (props?.isABFLPL) getDeviationHistory();
  }, []);

  return (
    <>
      <div className="sticky top-[-40px] bg-white">
        <Title
          back={props?.sliderBack}
          title="Loan Term Timeline"
          close={() => props?.setIsSliderOpen(false)}
          backAction={() =>
            props?.setCurrentSlider("CHANGE", props?.timelineInfo)
          }
        />

        {/* <div className="mt-6 text-sm font-medium text-[#4C4C4D] pb-5 border-b">
          <div className="flex justify-between items-center mb-1">
            <div>Loan Amount</div>
            <div>
              {getFormattedCurrency(parseFloat(timelineInfo[0]?.loanAmount?.previous)) || "-"}
            </div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div>Tenure</div>
            <div>{timelineInfo[0]?.tenure?.previous || "-"} Months</div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div>Rate of Interest %</div>
            <div>{timelineInfo[0]?.interestRate?.previous}%</div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div>Processing Fee</div>
            <div>{timelineInfo[0]?.processingFee?.previous}</div>
          </div>
          <div className="flex justify-between items-center mb-1">
            <div>Loan Offer Date</div>
            <div>{getFormattedDate(timelineInfo[0]?.createdAt)}</div>
          </div>
        </div> */}
      </div>

      {timelineInfo?.length > 0 ? (
        timelineInfo?.map((info: TimelineRow, index: number) => (
          <div
            className="mt-5 pb-5 border-b"
            key={index}
          >
            <div className="flex justify-between items-center mb-2 text-xs">
              <div className="text-[#626262] font-medium">Raised by</div>
              <div className="text-[#8D8D8E] font-normal">{info?.actionBy}</div>
            </div>
            <div className="flex justify-between items-center mb-2 text-xs">
              <div className="text-[#626262] font-medium">Status</div>
              <div
                className={classNames(
                  "font-medium capitalize",
                  TEXT_COLOR[info?.status as keyof typeof TEXT_COLOR] ??
                    "text-[#3290FE]"
                )}
              >
                {info?.status}
              </div>
            </div>
            <div className="flex justify-between items-center text-xs">
              <div className="text-[#626262] font-medium">Updated by</div>
              <div className="text-[#8D8D8E] font-normal">
                {getFormattedDate(info?.createdAt) ?? "-"}
              </div>
            </div>

            <div className="flex justify-between items-center text-sm mt-3">
              <div className="text-[#777878] font-normal">
                Requested Rate of Interest
              </div>
              <div className="text-[#4C4C4D] font-medium">
                {info?.interestRate?.proposed || "-"}
                {info?.interestRate?.proposed ? "%" : ""}
              </div>
            </div>
            <div className="flex justify-between items-center text-sm mt-3">
              <div className="text-[#777878] font-normal">Processing Fee</div>
              <div className="text-[#4C4C4D] font-medium">
                {info?.processingFee?.proposed
                  ? getFormattedCurrency(Number(info?.processingFee?.proposed))
                  : "-"}
              </div>
            </div>

            <div className="flex text-xs mt-3 flex-col">
              <div className="text-[#626262] font-medium">
                Reason for Deviation{" "}
              </div>
              <div className="text-[#777878] font-normal mt-1 whitespace-pre-wrap">
                {info?.comments ?? "-"}
              </div>
            </div>
          </div>
        ))
      ) : (
        // This block was in PL Only but showing in BL as well now.
        <div className="text-center text-sm text-[#777878] font-normal mt-5">
          No Deviation history found
        </div>
      )}
    </>
  );
}
